<script setup lang="ts" generic="T extends BankAccountSuggestionData">

import { ref, computed } from 'vue';
import type { BankAccountSuggestionData, HandlungBausteinProps } from '@/api/handlungen/types.ts';
import { DsAccordion, DsAlert, DsButton, NotificationVariant, useNotification } from '@demvsystems/design-components';
import CreateBankAccountModal from '@/user/components/CreateBankAccountModal.vue';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events.ts';
import { closeHandlung } from '@/api/handlungen/handlungenApi.ts';
import { Finanzmanager } from '@/application/router/types/types.ts';
import { useHandlungStore } from '@/handlungen/store/handlungStore.ts';
import { useRouter } from 'vue-router';
import { call } from '@/api/lib/integration.ts';
import { getIbanDetails } from '@/api/bankAccount/bankAccountsApi.ts';
import type { MinimumBankAccount } from '@/api/bankAccount/types.ts';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
import { captureException } from '@sentry/vue';

const { baustein, handlungId } = defineProps<HandlungBausteinProps<T>>();

const showCreateModal = ref<boolean>(false);
const ibanDetailsError = ref<boolean>(false);
const initData = ref<MinimumBankAccount | null>(null)

const router = useRouter();
const { duzen } = useBaseDataStore();
const { fetchHandlungenSilently } = useHandlungStore();

const formattedIban = computed(() => {
  const iban = (baustein?.data?.iban ?? '').toString().replaceAll(/\s+/g, '');
  if (!iban) return '';

  return iban.match(/.{1,4}/g)?.join(' ') ?? iban;
});

function handleBankAccountSuggestion() {
  if (!initData.value) {
    useNotification().show('Bankverbindung anlegen nicht möglich.', NotificationVariant.Error)
    return;
  }

  showCreateModal.value = true;
  useTagManager().push({ event: TrackEventCategory.BANK_ACCOUNT_SUGGESTION, action: 'check' })
}

async function handleClose() {
  await closeHandlung({ handlungId });
  useTagManager().push({ event: TrackEventCategory.BANK_ACCOUNT_SUGGESTION, action: 'reject' });
  await fetchHandlungenSilently();
  await router.push({ name: Finanzmanager.HANDLUNGEN });
}

async function generateIbanDetails() {
  await call(getIbanDetails({ iban: baustein.data.iban }),
    (data) => {
      if ('bic' in data) {
        initData.value = {
          depositor: baustein.data.ownerName,
          note: '',
          bankName: baustein.data.bankName,
          iban: baustein.data.iban,
          bic: data.bic
        }
      }

    },
    (error) => {
      ibanDetailsError.value = true;
      captureException(error);
    },
  );
}

generateIbanDetails()
</script>

<template>
  <div class="flex flex-col h-full">
    <DsAlert type="info" class="pt-3">
      <DsAccordion>
        <template #trigger>Wofür das Konto hinzufügen?</template>
        <template #default>
          <ul class="pt-2 space-y-2">
            <li>
              {{ duzen('Du hast', 'Sie haben') }} kürzlich dieses Bankkonto im Banking Bereich hinzugefügt. Um
              auch {{ duzen('deinem', 'Ihrem') }} Makler dieses Konto mitzuteilen, {{ duzen('füge', 'fügen Sie') }} es
              hier hinzu.
            </li>
            <li>
              {{ duzen('Dein', 'Ihr') }} Makler erhält keinen Zugriff oder Einblick in das Konto. Er kann das
              Konto nur nutzen, um Versicherungsgesellschaften mitzuteilen, von welchem Konto sie die Beiträge
              einziehen sollen.
            </li>
            <li>
              Wenn {{ duzen('du deinen', 'Sie Ihren') }} Makler nicht über dieses Konto informieren
              {{ duzen('möchtest, verwerfe', 'möchten, verwerfen Sie') }} diesen Vorschlag.
            </li>
          </ul>
        </template>
      </DsAccordion>
    </DsAlert>

    <DsAlert v-if="ibanDetailsError" type="error" label="Fehler aufgetreten" class="mt-5">
      Es kann aktuell keine Bankverbindung hinzugefügt werden. Bitte {{ duzen('versuche', 'versuchen Sie') }} es später
      nochmal oder {{ duzen('kontaktiere deinen', 'kontaktieren Sie Ihren') }} Makler.
    </DsAlert>

    <template v-else>
      <div class="flex flex-col my-8">
        <p class="text-gray-900 font-medium text-lg mb-0">IBAN</p>
        <p class="text-gray-900 text-lg mb-0">{{ formattedIban }}</p>
        <p class="text-gray-700 mb-0">{{ baustein.data.bankName }}</p>
      </div>

      <div class="flex flex-col-reverse md:flex-row items-center justify-end gap-5">
        <DsButton
          size="lg"
          class="mt-auto w-full md:w-auto"
          variant="secondary"
          :handler="handleClose"
        >
          Verwerfen
        </DsButton>
        <DsButton
          size="lg"
          class="mt-auto w-full md:w-auto"
          @click="handleBankAccountSuggestion"
        >
          Bankverbindung hinzufügen
        </DsButton>
      </div>
    </template>

    <CreateBankAccountModal
      v-if="initData"
      v-model:show="showCreateModal"
      :init-data="initData"
    />
  </div>
</template>
